/**
 * Global debug configuration - controls console log verbosity
 */

export type DebugLevel = 'off' | 'critical' | 'verbose';

class DebugConfig {
  private _level: DebugLevel = 'off';

  constructor() {
    this.loadLevel();
    this.setupGlobalHelper();
  }

  private loadLevel(): void {
    // Check environment variable first
    const envLevel = import.meta.env.VITE_DEBUG_LEVEL as DebugLevel;
    if (envLevel && ['off', 'critical', 'verbose'].includes(envLevel)) {
      this._level = envLevel;
      return;
    }

    // Check localStorage
    try {
      const storedLevel = localStorage.getItem('debug:level') as DebugLevel;
      if (storedLevel && ['off', 'critical', 'verbose'].includes(storedLevel)) {
        this._level = storedLevel;
      }
    } catch {
      // localStorage not available, use default
    }
  }

  private setupGlobalHelper(): void {
    if (typeof window !== 'undefined') {
      (window as any).__debug = (level: DebugLevel) => {
        this.setLevel(level);
        console.log(`🔧 Debug level set to: ${level}`);
      };
    }
  }

  get level(): DebugLevel {
    return this._level;
  }

  setLevel(level: DebugLevel): void {
    this._level = level;
    try {
      localStorage.setItem('debug:level', level);
    } catch {
      // localStorage not available
    }
  }

  isOff(): boolean {
    return this._level === 'off';
  }

  isCritical(): boolean {
    return this._level === 'critical' || this._level === 'verbose';
  }

  isVerbose(): boolean {
    return this._level === 'verbose';
  }

  shouldLog(level: 'critical' | 'verbose'): boolean {
    if (this._level === 'off') return false;
    if (level === 'critical') return this.isCritical();
    if (level === 'verbose') return this.isVerbose();
    return false;
  }
}

export const debugConfig = new DebugConfig();

// Helper functions for easy use
export const isVerbose = () => debugConfig.isVerbose();
export const isCritical = () => debugConfig.isCritical();
export const shouldLog = (level: 'critical' | 'verbose') => debugConfig.shouldLog(level);